// 表情生成服务 - 为角色生成不同表情的立绘
import { DoubaoImageServiceV2 } from './doubaoImageServiceV2.js'
import enhancedStorageService from './enhancedStorageService.js'
import { v4 as uuidv4 } from 'uuid'

class ExpressionService {
  constructor() {
    this.db = null
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      this.db = await enhancedStorageService.ensureDB()
    }
    return this.db
  }

  /**
   * 生成表情立绘
   * @param {Object} character - 角色信息
   * @param {string} expressionType - 表情类型
   * @returns {string} 生成的图片URL
   */
  async generateExpression(character, expressionType) {
    try {
      // 构建表情提示词
      const prompt = this.buildExpressionPrompt(character, expressionType)
      
      // 调用AI生成图片
      const imageUrl = await DoubaoImageServiceV2.generateImage(prompt, {
        aspectRatio: '1:1',
        maxImages: 1
      })
      
      return imageUrl
    } catch (error) {
      console.error('❌ 生成表情立绘失败:', error)
      throw error
    }
  }

  /**
   * 构建表情提示词
   */
  buildExpressionPrompt(character, expressionType) {
    const name = character.name || '角色'
    const personality = character.personality || '友好、开朗'
    const description = character.description || ''
    
    // 表情描述映射
    const expressionMap = {
      neutral: 'neutral expression, calm, normal',
      happy: 'happy expression, smiling, cheerful, bright eyes',
      shy: 'shy expression, blushing, slightly embarrassed, gentle smile',
      angry: 'angry expression, frowning, serious, intense eyes',
      surprised: 'surprised expression, wide eyes, open mouth, shocked',
      sad: 'sad expression, downcast eyes, melancholic, gentle'
    }
    
    const expressionDesc = expressionMap[expressionType] || expressionMap.neutral
    
    const prompt = `single character portrait, ${name}, only one person, ${personality}, ${description}, ${expressionDesc}, full body portrait, facing camera directly, eye contact with viewer, as if talking to someone in front of the screen, natural pose, detailed character design, solo character, no other people, solid color background, clean background, professional illustration, high quality, detailed, 4k, anime style`
    
    return prompt
  }

  /**
   * 为角色生成所有基础表情
   * @param {Object} character - 角色信息
   * @returns {Object} 所有表情的URL映射
   */
  async generateAllExpressions(character) {
    const expressionTypes = ['neutral', 'happy', 'shy', 'angry', 'surprised', 'sad']
    const expressions = {}
    
    for (const type of expressionTypes) {
      try {
        const imageUrl = await this.generateExpression(character, type)
        expressions[type] = imageUrl
        
        // 保存到数据库
        await this.saveExpression(character.id, type, imageUrl, this.buildExpressionPrompt(character, type))
      } catch (error) {
        console.error(`生成${type}表情失败:`, error)
      }
    }
    
    return expressions
  }

  /**
   * 保存表情到数据库
   */
  async saveExpression(characterId, expressionType, imageUrl, prompt) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterExpressions'], 'readwrite')
      const store = transaction.objectStore('characterExpressions')

      const expressionData = {
        id: uuidv4(),
        characterId,
        expressionType,
        imageUrl,
        prompt,
        createdAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(expressionData)
        request.onsuccess = () => {
          console.log('✅ 表情已保存:', expressionData.id)
          resolve(expressionData)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存表情失败:', error)
      throw error
    }
  }

  /**
   * 获取角色的所有表情
   */
  async getCharacterExpressions(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterExpressions'], 'readonly')
      const store = transaction.objectStore('characterExpressions')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const expressions = {}
          ;(request.result || []).forEach(expr => {
            expressions[expr.expressionType] = expr.imageUrl
          })
          resolve(expressions)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取表情失败:', error)
      return {}
    }
  }
}

export default new ExpressionService()

